/**
 * OpenLayers Feature 속성 — Domain ↔ Map 경계
 *
 * canonical 키: stringSeq, stringDisplayNo (전역 seq / Array 내 번호)
 * legacy 키(solarStringSeq, solarStringId)는 읽기·쓰기 호환용 — 외부(대시보드·결함) 마이그레이션 전까지 유지
 */

export const PANEL_MAP_PROP = Object.freeze({
  STRING_SEQ: 'stringSeq',
  STRING_DISPLAY_NO: 'stringDisplayNo',
  ARRAY_SEQ: 'arraySeq',
  ARRAY_DISPLAY_NO: 'arrayDisplayNo',
  MODULE_SEQ: 'moduleSeq',
  MODULE_DISPLAY_NO: 'moduleDisplayNo',
  SECTION_SEQ: 'sectionSeq',
  /** @deprecated 읽기 폴백만 — 신규 코드에서 set 금지 */
  LEGACY_STRING_SEQ: 'solarStringSeq',
  LEGACY_STRING_DISPLAY_NO: 'solarStringId',
  LEGACY_ARRAY_SEQ: 'solarArrayId',
  LEGACY_MODULE_DISPLAY_NO: 'solarModuleId'
});

function readProp(feature, ...keys) {
  if (!feature?.get) {
    return null;
  }
  for (const key of keys) {
    const v = feature.get(key);
    if (v != null && v !== '') {
      return String(v);
    }
  }
  return null;
}

export function readStringSeq(feature) {
  return readProp(
    feature,
    PANEL_MAP_PROP.STRING_SEQ,
    PANEL_MAP_PROP.LEGACY_STRING_SEQ
  );
}

export function readStringDisplayNo(feature) {
  return readProp(
    feature,
    PANEL_MAP_PROP.STRING_DISPLAY_NO,
    PANEL_MAP_PROP.LEGACY_STRING_DISPLAY_NO
  );
}

export function readArraySeq(feature) {
  return readProp(feature, PANEL_MAP_PROP.ARRAY_SEQ, PANEL_MAP_PROP.LEGACY_ARRAY_SEQ);
}

export function readArrayDisplayNo(feature) {
  return readProp(feature, PANEL_MAP_PROP.ARRAY_DISPLAY_NO);
}

export function readModuleSeq(feature) {
  return readProp(
    feature,
    PANEL_MAP_PROP.MODULE_SEQ,
    'solarModuleSeq'
  );
}

/** Module 삭제·이동 시 — DB seq만으로 맵 피처 일치 (표시 번호 충돌 방지) */
export function isModuleMapFeatureWithSeq(feature, moduleSeq) {
  if (!feature?.get || moduleSeq == null || moduleSeq === '') {
    return false;
  }
  const featureType = (feature.get('featureType') || '').toLowerCase();
  if (featureType !== 'module') {
    return false;
  }
  const fSeq = readModuleSeq(feature);
  return fSeq != null && String(fSeq) === String(moduleSeq);
}

export function readModuleDisplayNo(feature) {
  return readProp(
    feature,
    PANEL_MAP_PROP.MODULE_DISPLAY_NO,
    PANEL_MAP_PROP.LEGACY_MODULE_DISPLAY_NO
  );
}

/**
 * String Domain → OL feature flat props (canonical + legacy 동시 기록)
 * @param {import('./panelHierarchyMappers.js').PanelStringDomain} domain
 * @param {{ sectionSeq?: *, arraySeq?: *, arrayDisplayNo?: * }} [ctx]
 */
export function buildStringMapFeatureProps(domain, ctx = {}) {
  const seq = domain?.seq;
  const displayNo = domain?.displayNo;
  const props = {
    [PANEL_MAP_PROP.STRING_SEQ]: seq,
    [PANEL_MAP_PROP.STRING_DISPLAY_NO]: displayNo,
    [PANEL_MAP_PROP.LEGACY_STRING_SEQ]: seq,
    [PANEL_MAP_PROP.LEGACY_STRING_DISPLAY_NO]: displayNo,
    rowNum: domain?.rowNum ?? null,
    colNum: domain?.colNum ?? null,
    featureType: 'String'
  };
  if (ctx.sectionSeq != null) props[PANEL_MAP_PROP.SECTION_SEQ] = ctx.sectionSeq;
  if (ctx.arraySeq != null) {
    props[PANEL_MAP_PROP.ARRAY_SEQ] = ctx.arraySeq;
    props[PANEL_MAP_PROP.LEGACY_ARRAY_SEQ] = ctx.arraySeq;
  }
  if (ctx.arrayDisplayNo != null) {
    props[PANEL_MAP_PROP.ARRAY_DISPLAY_NO] = ctx.arrayDisplayNo;
  }
  return props;
}

/** Module Domain → OL feature flat props */
export function buildModuleMapFeatureProps(moduleDomain, stringDomain, ctx = {}) {
  const stringSeq = stringDomain?.seq;
  const stringDisplayNo = stringDomain?.displayNo;
  const props = {
    [PANEL_MAP_PROP.MODULE_SEQ]: moduleDomain?.seq,
    [PANEL_MAP_PROP.MODULE_DISPLAY_NO]: moduleDomain?.displayNo,
    [PANEL_MAP_PROP.STRING_SEQ]: stringSeq,
    [PANEL_MAP_PROP.STRING_DISPLAY_NO]: stringDisplayNo,
    [PANEL_MAP_PROP.LEGACY_STRING_SEQ]: stringSeq,
    [PANEL_MAP_PROP.LEGACY_STRING_DISPLAY_NO]: stringDisplayNo,
    [PANEL_MAP_PROP.LEGACY_MODULE_DISPLAY_NO]: moduleDomain?.displayNo,
    solarStringName: stringDomain?.name ?? (stringDisplayNo != null ? `String ${stringDisplayNo}` : ''),
    featureType: 'Module'
  };
  if (ctx.sectionSeq != null) props[PANEL_MAP_PROP.SECTION_SEQ] = ctx.sectionSeq;
  if (ctx.arraySeq != null) {
    props[PANEL_MAP_PROP.ARRAY_SEQ] = ctx.arraySeq;
    props[PANEL_MAP_PROP.LEGACY_ARRAY_SEQ] = ctx.arraySeq;
  }
  if (ctx.arrayDisplayNo != null) {
    props[PANEL_MAP_PROP.ARRAY_DISPLAY_NO] = ctx.arrayDisplayNo;
  }
  if (stringDomain?.shapeGeog != null) props.stringShapeGeog = stringDomain.shapeGeog;
  if (stringDomain?.rowNum != null) props.rowNum = stringDomain.rowNum;
  if (stringDomain?.colNum != null) props.colNum = stringDomain.colNum;
  return props;
}

/** OL Feature에 canonical props 병합 (기존 feature 갱신용) */
export function applyMapFeatureProps(feature, props) {
  if (!feature?.set || !props) {
    return feature;
  }
  Object.entries(props).forEach(([key, value]) => {
    if (value !== undefined) {
      feature.set(key, value);
    }
  });
  return feature;
}

/** String seq/displayNo를 feature에 기록 (canonical + legacy) */
export function writeStringIdentityOnFeature(feature, { seq, displayNo }) {
  return applyMapFeatureProps(feature, {
    [PANEL_MAP_PROP.STRING_SEQ]: seq,
    [PANEL_MAP_PROP.STRING_DISPLAY_NO]: displayNo,
    [PANEL_MAP_PROP.LEGACY_STRING_SEQ]: seq,
    [PANEL_MAP_PROP.LEGACY_STRING_DISPLAY_NO]: displayNo
  });
}
